/**
 * Web group-setup wizard (founder) and snail-link join page (newcomer).
 *
 * Founder flow: /setup/<token> → claim form → #setup/<group_id> → step 1
 * (create supergroup + promote bot, browser GramJS) → step 2 (invite via
 * contacts picker / snail link; roster self-assembles from bot-detected
 * joins) → finalize → the group feed.
 *
 * Newcomer flow: /join/<token> → sign in (own GramJS session) → import the
 * hidden Telegram invite + /start g_<gid> onboarding.
 *
 * The GramJS/API calls live in setup.ts; this module is only the UI.
 */

import { apiFetch } from "./api.js";
import { escapeHtml } from "./util.js";
import { t } from "./i18n.js";
import { renderAuthForm } from "./auth.js";
import { maybeRenderEmailAsk } from "./email-ask.js";
import {
  addContactsToGroup,
  claimSetupToken,
  createSupergroup,
  finalizeSetup,
  getContacts,
  getSetupStatus,
  joinViaSnailLink,
  mintJoinLink,
  promoteBotInGroup,
  reportStep1,
  type ContactEntry,
  type SetupStatus,
} from "./setup.js";

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested)
// ---------------------------------------------------------------------------

/** Convert a wall-clock hour in a timezone (given as minutes behind UTC, i.e.
 *  Date.getTimezoneOffset() convention) to the UTC hour. */
export function utcHourFor(localHour: number, offsetMinutes: number): number {
  return (((localHour + offsetMinutes / 60) % 24) + 24) % 24;
}

/** Today's UTC hour for a local wall-clock hour. */
function localHourToUtc(localHour: number): number {
  return Math.round(utcHourFor(localHour, new Date().getTimezoneOffset()));
}

/** The snail join URL for a token, on this deployment's origin. */
export function joinUrl(origin: string, token: string): string {
  return `${origin}/join/${token}`;
}

/** Cadence <option>s for the claim form; every other week is preselected. */
export function cadenceOptions(): string {
  return [1, 2, 3, 4].map((n) => {
    const label = n === 1 ? t("setup.cadence_1")
      : n === 2 ? t("setup.cadence_rec", { n })
      : t("setup.cadence_n", { n });
    return `<option value="${n}"${n === 2 ? " selected" : ""}>${label}</option>`;
  }).join("");
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function errorBanner(root: HTMLElement, msg: string): void {
  const e = root.querySelector<HTMLElement>(".wizard-error");
  if (!e) return;
  e.style.display = "block";
  e.textContent = msg;
}

/** Map an apiFetch/GramJS error to user-facing copy where we recognize it. */
function describeError(e: any): string {
  const m = String(e?.message || e);
  if (m.includes("circle is full")) return t("setup.err_full");
  if (m.includes("Link expired")) return t("join.err_expired");
  if (m.includes("multi-person correspondence")) return t("setup.err_wall");
  if (m.includes("API 403")) return t("setup.err_token_claimed");
  if (m.includes("API 404") || m.includes("API 409")) return t("setup.err_token_invalid");
  const flood = /A wait of (\d+) seconds/.exec(m);
  if (flood) return t("setup.err_floodwait", { s: flood[1] });
  return m;
}

// ---------------------------------------------------------------------------
// Claim form (entry: /setup/<token>)
// ---------------------------------------------------------------------------

export async function renderSetupClaim(
  root: HTMLElement,
  client: any,
  token?: string,
): Promise<void> {
  const me = await client.getMe();
  const myName =
    [me.firstName, me.lastName].filter(Boolean).join(" ") || me.username || "";

  let hasAnyEmail = true;
  try {
    const state = await apiFetch("/me/email-state");
    hasAnyEmail = Boolean(state?.has_any_email);
  } catch {
    // If we can't tell, don't block circle creation on it — just ask.
    hasAnyEmail = false;
  }

  const hours = Array.from({ length: 24 }, (_, h) => {
    const utc = localHourToUtc(h);
    const sel = h === 17 ? " selected" : "";
    return `<option value="${utc}"${sel}>${String(h).padStart(2, "0")}:00 (${String(utc).padStart(2, "0")}:00 UTC)</option>`;
  }).join("");
  const cadences = cadenceOptions();

  root.innerHTML = `
    <div class="landing">
      <section class="landing-hero">
        <h2><img src="/logo.png" class="h2-logo" alt=""> ${t("setup.claim.title")}</h2>
        <p>${t("setup.claim.intro")}</p>
      </section>
      <section>
        <div class="form-group"><label>${t("setup.name")}</label>
          <input id="sw-name" type="text" maxlength="64"></div>
        <div class="form-group"><label>${t("setup.hour")} <span style="color:var(--muted)">${t("setup.hour_note")}</span></label>
          <select id="sw-hour">${hours}</select></div>
        <div class="form-group"><label>${t("setup.cadence")}</label>
          <select id="sw-cadence">${cadences}</select></div>
        ${hasAnyEmail ? "" : `
        <div class="form-group"><label>${t("setup.email")}</label>
          <input id="sw-email" type="email"></div>`}
        <button class="btn" id="sw-claim">${t("setup.create")}</button>
        <div class="error-banner wizard-error" style="display:none"></div>
      </section>
    </div>`;

  root.querySelector("#sw-claim")!.addEventListener("click", async () => {
    const name = root.querySelector<HTMLInputElement>("#sw-name")!.value.trim();
    const display = myName;
    if (!name) {
      errorBanner(root, t("setup.err_name_required"));
      return;
    }
    const btn = root.querySelector<HTMLButtonElement>("#sw-claim")!;
    btn.disabled = true;
    try {
      const res = await claimSetupToken({
        token,
        displayName: display,
        email: root.querySelector<HTMLInputElement>("#sw-email")?.value.trim() ?? "",
        groupName: name,
        publicationHourUtc: parseInt(root.querySelector<HTMLSelectElement>("#sw-hour")!.value, 10),
        cadenceWeeks: parseInt(root.querySelector<HTMLSelectElement>("#sw-cadence")!.value, 10),
      });
      // Leave the token path behind so refresh resumes the wizard, not the
      // (now consumed) claim form.
      window.history.replaceState(null, "", `/#setup/${res.group_id}`);
      await renderSetupWizard(root, client, res.group_id);
    } catch (e) {
      btn.disabled = false;
      errorBanner(root, describeError(e));
    }
  });
}

// ---------------------------------------------------------------------------
// Wizard steps (entry: #setup/<group_id>)
// ---------------------------------------------------------------------------

export async function renderSetupWizard(
  root: HTMLElement,
  client: any,
  groupId: string,
): Promise<void> {
  let status: SetupStatus;
  try {
    status = await getSetupStatus(groupId);
  } catch (e) {
    root.innerHTML = `<div class="landing"><p>${escapeHtml(describeError(e))}</p></div>`;
    return;
  }
  if (status.step === "finalized") {
    window.location.hash = `#g/${groupId}`;
    return;
  }
  if (status.step === "promote") return renderStepPromote(root, client, status);
  return renderStepInvite(root, client, status);
}

function stepsHeader(current: 1 | 2): string {
  const mark = (n: number, label: string) =>
    `<span class="wizard-step${n === current ? " active" : n < current ? " done" : ""}">${n < current ? "✓" : n}. ${label}</span>`;
  return `<div class="wizard-steps">
    ${mark(1, t("setup.steps.create"))} ${mark(2, t("setup.steps.invite"))}
  </div>`;
}

async function renderStepPromote(
  root: HTMLElement,
  client: any,
  status: SetupStatus,
): Promise<void> {
  root.innerHTML = `
    <div class="landing">
      <h2><img src="/logo.png" class="h2-logo" alt=""> ${escapeHtml(status.group_name)}</h2>
      ${stepsHeader(1)}
      <section>
        <h3>${t("setup.step1.title")}</h3>
        <p>${t("setup.step1.body")}</p>
        <button class="btn" id="sw-create">${t("setup.step1.go")}</button>
        <p id="sw-progress" style="color:var(--muted);display:none"></p>
        <div class="error-banner wizard-error" style="display:none"></div>
      </section>
    </div>`;

  root.querySelector("#sw-create")!.addEventListener("click", async () => {
    const btn = root.querySelector<HTMLButtonElement>("#sw-create")!;
    const progress = root.querySelector<HTMLElement>("#sw-progress")!;
    btn.disabled = true;
    progress.style.display = "block";
    try {
      progress.textContent = t("setup.step1.creating");
      const chatId = await createSupergroup(client, status.group_name);
      progress.textContent = t("setup.step1.promoting");
      await promoteBotInGroup(client, chatId);
      await reportStep1(status.group_id, chatId, status.group_name);
      await renderSetupWizard(root, client, status.group_id);
    } catch (e) {
      btn.disabled = false;
      progress.style.display = "none";
      errorBanner(root, describeError(e));
    }
  });
}

async function renderStepInvite(
  root: HTMLElement,
  client: any,
  status: SetupStatus,
): Promise<void> {
  root.innerHTML = `
    <div class="landing">
      <h2><img src="/logo.png" class="h2-logo" alt=""> ${escapeHtml(status.group_name)}</h2>
      ${stepsHeader(2)}
      <section>
        <h3>${t("setup.step2.title")}</h3>
        <p>${t("setup.step2.body")}</p>

        <div class="form-group" style="margin-top:1rem">
          <label>${t("setup.link.title")}</label>
          <div id="sw-link-area">
            <button class="btn" id="sw-mint">${t("setup.link.mint")}</button>
          </div>
        </div>

        <div class="form-group" style="margin-top:1rem">
          <label>${t("setup.contacts.title")}</label>
          <div id="sw-contacts-area">
            <button class="btn" id="sw-load-contacts">${t("setup.contacts.load")}</button>
          </div>
        </div>

        <div class="form-group" style="margin-top:1rem">
          <label>${t("setup.roster.title")}</label>
          <ul id="setup-roster" style="list-style:none"></ul>
        </div>

        <button class="btn" id="sw-finalize" disabled>${t("setup.finalize")}</button>
        <p style="color:var(--muted);font-size:.875rem" id="sw-finalize-note">${t("setup.finalize.need2")}</p>
        <div class="error-banner wizard-error" style="display:none"></div>
      </section>
    </div>`;

  const gid = status.group_id;
  const chatId = status.telegram_chat_id!;

  // --- Roster: poll while this view is mounted; self-assembles from joins. ---
  const refreshRoster = async () => {
    const list = document.getElementById("setup-roster");
    if (!list) return false;
    try {
      const g = await apiFetch(`/groups/${gid}`);
      const members = [...g.members].sort(
        (a: any, b: any) => a.rotation_position - b.rotation_position
      );
      list.innerHTML = members.map((m: any) => {
        const coverage = m.reachable
          ? `✓ connected${m.has_email ? " · ✉ email" : ""}`
          : `— not connected yet — have them tap the group link before you finalize`;
        return `<li style="padding:.35rem 0;border-bottom:1px solid var(--border)">
           ${escapeHtml(m.display_name)} ${m.is_you ? t("setup.roster.you") : ""}
           <span style="color:var(--muted);font-size:.875rem"> ${coverage}</span>
         </li>`;
      }).join("") || `<li style="color:var(--muted)">${t("setup.roster.waiting")}</li>`;
      const fin = root.querySelector<HTMLButtonElement>("#sw-finalize");
      const note = root.querySelector<HTMLElement>("#sw-finalize-note");
      if (fin) fin.disabled = members.length < 2;
      if (note && members.length >= 2) note.textContent = t("setup.finalize.note");
    } catch { /* transient; next poll retries */ }
    return true;
  };
  await refreshRoster();
  const timer = window.setInterval(async () => {
    if (!(await refreshRoster())) window.clearInterval(timer);
  }, 4000);

  // --- Snail link ---
  root.querySelector("#sw-mint")!.addEventListener("click", async () => {
    const area = root.querySelector<HTMLElement>("#sw-link-area")!;
    try {
      const res = await mintJoinLink(gid);
      if (!res.ok || !res.snail_token) throw new Error(res.error || "mint failed");
      const url = joinUrl(window.location.origin, res.snail_token);
      area.innerHTML = `
        <input type="text" readonly value="${escapeHtml(url)}" id="sw-link" style="width:100%">
        <button class="btn" id="sw-copy" style="margin-top:.5rem">${t("setup.link.copy")}</button>`;
      area.querySelector("#sw-copy")!.addEventListener("click", async () => {
        await navigator.clipboard.writeText(url);
        area.querySelector("#sw-copy")!.textContent = t("setup.link.copied");
      });
    } catch (e) {
      errorBanner(root, describeError(e));
    }
  });

  // --- Contacts picker (lazy: loading everyone's contact list unprompted
  // would be both slow and presumptuous) ---
  root.querySelector("#sw-load-contacts")!.addEventListener("click", async () => {
    const area = root.querySelector<HTMLElement>("#sw-contacts-area")!;
    area.innerHTML = `<p style="color:var(--muted)">${t("setup.contacts.loading")}</p>`;
    let contacts: ContactEntry[];
    try {
      contacts = await getContacts(client);
    } catch (e) {
      area.innerHTML = "";
      errorBanner(root, describeError(e));
      return;
    }
    contacts.sort((a, b) => a.name.localeCompare(b.name));
    // Search matches name, @username and phone; the secondary line shows
    // username/phone so same-named contacts are tellable apart.
    const haystack = (c: ContactEntry) =>
      [c.name, c.username, c.phone].filter(Boolean).join(" ").toLowerCase();
    const detail = (c: ContactEntry) =>
      [c.username && `@${c.username}`, c.phone && `+${c.phone}`]
        .filter(Boolean)
        .join(" · ");
    area.innerHTML = `
      <input type="text" id="sw-search" placeholder="${t("setup.contacts.search")}" style="width:100%">
      <ul id="sw-contact-list" style="list-style:none;max-height:14rem;overflow-y:auto;margin-top:.5rem">
        ${contacts.map((c, i) => `
          <li data-search="${escapeHtml(haystack(c))}" style="padding:.25rem 0">
            <label style="font-weight:normal;display:flex;gap:.5rem;align-items:baseline">
              <input type="checkbox" data-idx="${i}">
              <span style="color:var(--fg)">${escapeHtml(c.name)}</span>
              <span style="font-size:.8rem">${escapeHtml(detail(c))}</span>
            </label>
          </li>`).join("")}
      </ul>
      <button class="btn" id="sw-add">${t("setup.contacts.add")}</button>`;

    area.querySelector<HTMLInputElement>("#sw-search")!.addEventListener("input", (ev) => {
      const q = (ev.target as HTMLInputElement).value.toLowerCase();
      area.querySelectorAll<HTMLElement>("#sw-contact-list li").forEach((li) => {
        li.style.display = (li.dataset.search || "").includes(q) ? "" : "none";
      });
    });

    area.querySelector("#sw-add")!.addEventListener("click", async () => {
      const picked = Array.from(
        area.querySelectorAll<HTMLInputElement>("input[type=checkbox]:checked")
      ).map((cb) => contacts[parseInt(cb.dataset.idx!, 10)]);
      if (picked.length === 0) return;
      const btn = area.querySelector<HTMLButtonElement>("#sw-add")!;
      btn.disabled = true;
      try {
        const failed = await addContactsToGroup(client, chatId, picked);
        btn.disabled = false;
        btn.textContent = t("setup.contacts.added");
        if (failed.length > 0) {
          const names = picked.filter((c) => failed.includes(c.id)).map((c) => c.name);
          errorBanner(root, t("setup.contacts.failed", { names: names.join(", ") }));
        }
        area.querySelectorAll<HTMLInputElement>("input[type=checkbox]:checked")
          .forEach((cb) => { cb.checked = false; });
        await refreshRoster();
      } catch (e) {
        btn.disabled = false;
        errorBanner(root, describeError(e));
      }
    });
  });

  // --- Finalize ---
  root.querySelector("#sw-finalize")!.addEventListener("click", async () => {
    const btn = root.querySelector<HTMLButtonElement>("#sw-finalize")!;
    btn.disabled = true;
    try {
      await finalizeSetup(gid);
      window.clearInterval(timer);
      window.location.hash = `#g/${gid}`;
      window.location.reload(); // fresh sidebar + feed for the now-active group
    } catch (e) {
      btn.disabled = false;
      errorBanner(root, describeError(e));
    }
  });
}

// ---------------------------------------------------------------------------
// Join page (entry: /join/<token>)
// ---------------------------------------------------------------------------

export function renderJoinPage(
  root: HTMLElement,
  client: any | null,
  token: string,
): void {
  root.innerHTML = `
    <div class="landing">
      <section class="landing-hero">
        <h2><img src="/logo.png" class="h2-logo" alt=""> ${t("join.title")}</h2>
        <p>${t("join.intro")}</p>
      </section>
      <section id="join-action"></section>
      <div class="error-banner wizard-error" style="display:none"></div>
    </div>`;
  const action = root.querySelector<HTMLElement>("#join-action")!;

  if (!client) {
    action.innerHTML = `
      <h3>${t("join.signin_title")}</h3>
      <p class="landing-signin-note">${t("join.signin_note")}</p>
      <div id="auth-form"></div>`;
    // Reload after login: the /join/<token> path is preserved, so the app
    // re-enters here with a live client.
    renderAuthForm(action.querySelector<HTMLElement>("#auth-form")!, () =>
      window.location.reload()
    );
    return;
  }

  action.innerHTML = `<button class="btn" id="join-btn">${t("join.join")}</button>`;
  action.querySelector("#join-btn")!.addEventListener("click", async () => {
    const btn = action.querySelector<HTMLButtonElement>("#join-btn")!;
    btn.disabled = true;
    btn.textContent = t("join.joining");
    try {
      await joinViaSnailLink(client, token);
    } catch (e: any) {
      // Already in the group → the /start onboarding may simply not have
      // happened yet; fall through to the success view.
      if (!String(e?.message || e).includes("USER_ALREADY_PARTICIPANT")) {
        btn.disabled = false;
        btn.textContent = t("join.join");
        errorBanner(root, describeError(e));
        return;
      }
    }
    action.innerHTML = `
      <h3>${t("join.done.title")}</h3>
      <p>${t("join.done.body")}</p>
      <div id="join-email-ask"></div>`;
    await maybeRenderEmailAsk(
      action.querySelector<HTMLElement>("#join-email-ask")!,
      () => {
        action.innerHTML = `
          <h3>${t("join.done.title")}</h3>
          <p>${t("join.done.body")}</p>
          <a class="btn" href="/" style="display:inline-block;margin-top:1rem">${t("join.open_app")}</a>`;
      }
    );
  });
}
